import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import logo from '../assets/new-logo.png';
import LoginTermsModal from './LoginTermsModal';
import websocketService from '../services/websocketService';
import { useTelemetry } from '../hooks/useTelemetry';
import { MODULES } from '../telemetry/constants/modules';
import { ACTIONS } from '../telemetry/constants/actions';
import { telemetryManager } from '../telemetry/TelemetryManager';

type AccountType = 'brand' | 'agency';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
    const navigate = useNavigate();
    const { trackAction } = useTelemetry({ module: MODULES.AUTHENTICATION, submodule: 'Login' });
    const [accountType, setAccountType] = useState<AccountType>('brand');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const logoUrl = (() => {
        try {
            return JSON.parse(localStorage.getItem('logo_url') || 'null');
        } catch {
            return localStorage.getItem('logo_url');
        }
    })();

    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [pendingLoginResponse, setPendingLoginResponse] = useState<any>(null);

    console.log(logoUrl);
    const isBrand = accountType === 'brand';

    const completeLogin = (response: any) => {
        // Clear stale cross-role keys before writing the new session
        if (isBrand) {
            localStorage.removeItem('agency_id');
        } else {
            localStorage.removeItem('user_id');
        }

        localStorage.setItem('access_token', response.token || '');
        localStorage.setItem('user_id', response.user_id || '');
        localStorage.setItem('username', response.username || '');
        websocketService.connect(response.username);
        localStorage.setItem('full_name', response.full_name || '');
        localStorage.setItem('account_type', accountType);
        localStorage.setItem('logo_url', (response.logo_url || '').replace(/"/g, ''));
        if (response.business_name || response.brand_name) {
            localStorage.setItem('brand_name', response.brand_name || response.business_name || '');
        }

        // Force a fresh session for telemetry to embed the new login timestamp
        telemetryManager.resetSession();

        // Trigger login telemetry
        trackAction(ACTIONS.LOGIN, 'Login Successful', { accountType });

        if (!isBrand) {
            localStorage.setItem('agency_id', response.user_id || '');
        }

        onClose();
        navigate(isBrand ? '/marketing-dashboard' : '/agency-dashboard');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            let response: any;

            if (isBrand) {
                response = await apiService.login({ username, password });
            } else {
                response = await apiService.agencyLogin({ email: username, password });
            }

            if (response.status === 'success') {
                if (isBrand && response.latest_terms_accepted === false) {
                    setPendingLoginResponse(response);
                    setTermsModalOpen(true);
                } else {
                    completeLogin(response);
                }
            } else {
                setError(response.message || 'Login failed');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                {/* Header */}
                <div className={`px-6 py-8 text-center transition-all duration-300 ${isBrand
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600'
                    : 'bg-gradient-to-r from-teal-600 to-blue-600'
                    }`}>
                    <img src={logo} alt="Marketing 4Sight" className="h-24 w-auto object-contain mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white">Welcome Back</h2>
                    <p className="text-blue-100 mt-2">
                        Sign in as {isBrand ? 'Brand' : 'Agency'}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Brand / Agency Toggle */}
                    <div className="flex bg-gray-100 rounded-xl p-1">
                        <button
                            type="button"
                            onClick={() => setAccountType('brand')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${isBrand
                                ? 'bg-white text-blue-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <i className="ri-building-line"></i> Brand
                        </button>
                        <button
                            type="button"
                            onClick={() => setAccountType('agency')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${!isBrand
                                ? 'bg-white text-teal-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <i className="ri-team-line"></i> Agency
                        </button>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                            {isBrand ? 'Username' : 'Agency Email'}
                        </label>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder={isBrand ? 'your username' : 'you@example.com'}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                            Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors pr-12"
                                placeholder="Enter your password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                            >
                                <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${isBrand
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                            : 'bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700'
                            }`}
                    >
                        {isLoading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Signing in...
                            </>
                        ) : (
                            `Sign In as ${isBrand ? 'Brand' : 'Agency'}`
                        )}
                    </button>
                </form>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
                    <p className="text-sm text-gray-600">
                        Don't have an account?{' '}
                        <button
                            onClick={() => {
                                onClose();
                                navigate('/onboarding');
                            }}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                            Start Onboarding
                        </button>
                    </p>
                </div>

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            
            {pendingLoginResponse && (
                <LoginTermsModal
                    open={termsModalOpen}
                    userId={pendingLoginResponse.user_id}
                    email={pendingLoginResponse.email || pendingLoginResponse.username}
                    onClose={() => {
                        setTermsModalOpen(false);
                        setPendingLoginResponse(null);
                    }}
                    onAccept={() => {
                        setTermsModalOpen(false);
                        completeLogin(pendingLoginResponse);
                        setPendingLoginResponse(null);
                    }}
                />
            )}
        </div>
    );
}
